import { useState } from 'react';

import { de } from '../../i18n/de';
import { TapToSpeakOffer } from '../../components/TapToSpeakButton';
import { ALPHABET_GROUPS } from '../../data/seed/alphabetGroups';
import { useComposerStore } from '../../core/composer/useComposer';
import { getSettings } from '../../core/profile/ProfileStore';
import { speechService } from '../../core/speech/SpeechService';

/**
 * Partner-assisted scanning (1.15) — the software floor.
 *
 * Shipping this in Phase 1 rather than with the other input modes is deliberate: it is what works on
 * the days when nothing else does, and those days will arrive long before Phase 3. Fatigue, a bad
 * afternoon, a hand that will not cooperate — the person still needs to be able to spell a word.
 *
 * The flow: the caregiver reads a group aloud ("E, N, I, S, R?"), the person answers with one of two
 * **half-screen** targets. Yes narrows to the letters in that group, asked one at a time; no advances
 * to the next group. A wrong guess costs one question.
 *
 * Groups come from `data/seed/alphabetGroups.ts`, which is the same data the printed card is
 * generated from (1.16). A card whose sequence disagrees with the screen would be worse than no card,
 * because the caregiver would have learned the wrong one.
 *
 * Two targets, each half the screen, is as large as a target can be. There is nothing else on this
 * surface competing for the space, and that is the point.
 */

type Stage = { kind: 'group'; index: number } | { kind: 'letter'; group: number; letter: number };

export function PartnerAssisted({ onExit }: { onExit: () => void }): React.JSX.Element {
  const [stage, setStage] = useState<Stage>({ kind: 'group', index: 0 });
  const composer = useComposerStore();

  const group = ALPHABET_GROUPS[stage.kind === 'group' ? stage.index : stage.group];
  const letter =
    stage.kind === 'letter' ? (group?.letters[stage.letter] ?? null) : null;

  const answerYes = (): void => {
    if (stage.kind === 'group') {
      setStage({ kind: 'letter', group: stage.index, letter: 0 });
      return;
    }
    if (letter !== null) {
      // No `speakKeyEcho` here, and that asymmetry with the keyboard is deliberate: in this mode the
      // caregiver has just read the letter out loud — that is what the mode *is* — so a second voice
      // saying it again would be noise over the one thing the person is listening to.
      composer.insertTyped(letter);
      // Back to the first group for the next letter: the person spells forwards, and restarting from
      // a predictable place costs the caregiver nothing to remember.
      setStage({ kind: 'group', index: 0 });
    }
  };

  const answerNo = (): void => {
    if (stage.kind === 'group') {
      // Wrap round rather than stopping: the letter is in one of them, and a dead end would mean
      // starting the whole word again.
      setStage({ kind: 'group', index: (stage.index + 1) % ALPHABET_GROUPS.length });
      return;
    }
    const next = stage.letter + 1;
    if (next < (group?.letters.length ?? 0)) {
      setStage({ kind: 'letter', group: stage.group, letter: next });
    } else {
      // Exhausted the group — the yes was a mistake. Return to the groups rather than insisting.
      setStage({ kind: 'group', index: 0 });
    }
  };

  return (
    <div
      data-testid="partner-assisted"
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--bg)',
        color: 'var(--fg)',
        padding: 'calc(var(--safe-top) + 4px) 8px calc(var(--safe-bottom) + 4px)',
        gap: 8,
      }}
    >
      <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ flex: '1 1 auto', fontSize: 'var(--font-body)' }}>
          {composer.state.text || de.partnerAssistedTitle}
        </span>
        <button
          type="button"
          data-testid="partner-assisted-speak"
          onClick={() => {
            const text = composer.text();
            if (text === '') return;
            void speechService.speak({ text, source: 'keyboard' }).then((result) => {
              // Same setting as the keyboard's Sprechen: a word spelled out one question at a time is
              // the last thing that should vanish because it was spoken once.
              //
              // No keep offer here, deliberately (`AUT-05`): this surface replaces the frame and has no
              // contextual band to put the control in, and a sentence spelled out letter by letter with a
              // partner is one the caregiver can keep in care mode afterwards.
              if (result) void composer.commit('keyboard', { keepText: getSettings().keepTextAfterSpeak });
            });
          }}
          style={smallButton}
        >
          {de.speak}
        </button>
        <button type="button" data-testid="partner-assisted-exit" onClick={onExit} style={smallButton}>
          {de.back}
        </button>
      </div>

      {/* The repair offer (`SUR-24`). This surface replaces the frame, so it has neither the contextual
          band nor the audio banner — and this is the mode used on the worst days, which is exactly when
          being unable to fix the sound would matter most. It renders nothing until something has failed. */}
      <TapToSpeakOffer />

      {/* What the caregiver reads aloud. Large, because they are often standing and not close. */}
      <div
        data-testid="partner-assisted-prompt"
        style={{
          flex: '0 0 auto',
          textAlign: 'center',
          fontSize: 'calc(var(--font-output) * 0.8)',
          fontWeight: 700,
        }}
      >
        {stage.kind === 'group' ? group?.label : letter === ' ' ? de.space : letter}
      </div>
      <p style={{ flex: '0 0 auto', textAlign: 'center', fontSize: 'var(--font-body)' }}>
        {stage.kind === 'group' ? de.partnerAssistedGroupQuestion : de.partnerAssistedLetterQuestion}
      </p>

      {/* Half the screen each. Nothing else is on this surface, so nothing else needs the room. */}
      <div style={{ flex: '1 1 auto', minHeight: 0, display: 'flex', gap: 8 }}>
        <button type="button" data-testid="partner-assisted-yes" onClick={answerYes} style={halfButton}>
          {de.yes}
        </button>
        <button
          type="button"
          data-testid="partner-assisted-no"
          onClick={answerNo}
          style={{ ...halfButton, background: 'var(--key-bg)', color: 'var(--key-fg)' }}
        >
          {de.no}
        </button>
      </div>
    </div>
  );
}

const smallButton: React.CSSProperties = {
  minHeight: 'var(--min-target)',
  paddingInline: 12,
  fontSize: 'var(--font-body)',
  background: 'var(--key-bg)',
  color: 'var(--key-fg)',
  border: '3px solid var(--key-border)',
  borderRadius: 'var(--radius)',
};

const halfButton: React.CSSProperties = {
  flex: '1 1 0',
  minWidth: 0,
  fontSize: 'var(--font-output)',
  fontWeight: 700,
  background: 'var(--accent)',
  color: 'var(--accent-fg)',
  border: '4px solid var(--key-border)',
  borderRadius: 'var(--radius)',
};
